import { useEffect, useMemo } from "react";

const REDUCED_VISUAL_EFFECTS_CLASS = "reduced-visual-effects";

function getReducedEffectsOverride() {
  if (typeof window === "undefined") return null;

  const value = new URLSearchParams(window.location.search).get("reducedEffects");
  if (value === "1" || value === "true") return true;
  if (value === "0" || value === "false") return false;
  return null;
}

function getClientHintsText() {
  const userAgentData = (navigator as Navigator & {
    userAgentData?: {
      brands?: Array<{ brand: string; version: string }>;
      platform?: string;
      mobile?: boolean;
    };
  }).userAgentData;

  if (!userAgentData) return "";

  return [
    userAgentData.platform,
    userAgentData.mobile ? "mobile" : "",
    ...(userAgentData.brands ?? []).map((brand) => brand.brand),
  ]
    .filter(Boolean)
    .join(" ");
}

function shouldReduceVisualEffects() {
  if (typeof navigator === "undefined") return false;

  const override = getReducedEffectsOverride();
  if (override !== null) return override;

  const userAgent = navigator.userAgent.toLowerCase();
  const clientHints = getClientHintsText().toLowerCase();
  const source = `${userAgent} ${clientHints}`;

  const isAndroid = source.includes("android");
  // Fallback direcionado para travamentos visuais observados em WebView Android e POCO/Xiaomi.
  // Manter conservador: Chrome Android comum nao deve perder blur/efeitos por padrao.
  const isWebView =
    source.includes("; wv") ||
    (isAndroid && source.includes(" version/") && !source.includes("chrome/")) ||
    (isAndroid && source.includes("metajuntos"));
  const isXiaomiFamily =
    source.includes("xiaomi") ||
    source.includes("redmi") ||
    source.includes("poco") ||
    source.includes("miui");

  return isAndroid && (isWebView || isXiaomiFamily);
}

export function useReducedVisualEffects() {
  const reduceVisualEffects = useMemo(shouldReduceVisualEffects, []);

  useEffect(() => {
    const root = document.documentElement;

    root.classList.toggle(REDUCED_VISUAL_EFFECTS_CLASS, reduceVisualEffects);
    root.dataset.reducedVisualEffects = reduceVisualEffects ? "true" : "false";

    return () => {
      root.classList.remove(REDUCED_VISUAL_EFFECTS_CLASS);
      delete root.dataset.reducedVisualEffects;
    };
  }, [reduceVisualEffects]);

  return reduceVisualEffects;
}
